import React, { FC, useEffect } from 'react'
import { Box, TextField, Select, MenuItem, Typography, Stack, CircularProgress } from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs, { Dayjs } from 'dayjs'
import FileUploadSection from './FileUploadSection'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import { fetchSites } from '@/features/sites/sitesThunks'
import { fetchContentTags } from '@/features/options/optionsThunks'
import { selectContentTags, selectOptionsLoading } from '@/features/options/optionsSelectors'

interface NewsFormFieldsProps {
  title: string
  tag: string
  site: string
  date: Dayjs | null
  dateError: boolean
  contentType: string
  image: File | string | null
  document: File | string | null
  onTitleChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onTagChange: (e: any) => void
  onSiteChange: (e: any) => void
  onDateChange: (d: Dayjs | null) => void
  onImageSelect: (e: React.ChangeEvent<HTMLInputElement>) => void
  onDocumentSelect: (e: React.ChangeEvent<HTMLInputElement>) => void
  onRemoveImage: () => void
  onRemoveDocument: () => void
}

const NewsFormFields: FC<NewsFormFieldsProps> = ({
  title,
  tag,
  site,
  date,
  dateError,
  contentType,
  image,
  document,
  onTitleChange,
  onTagChange,
  onSiteChange,
  onDateChange,
  onImageSelect,
  onDocumentSelect,
  onRemoveImage,
  onRemoveDocument,
}) => {
  const dispatch = useAppDispatch()
  const { sites, loading: sitesLoading } = useAppSelector((state) => state.sites)
  const contentTags = useAppSelector(selectContentTags)
  const tagsLoading = useAppSelector(selectOptionsLoading)

  // Fetch sites and tags on component mount
  useEffect(() => {
    if (sites.length === 0) {
      dispatch(fetchSites())
    }
    if (contentTags.length === 0) {
      dispatch(fetchContentTags())
    }
  }, [dispatch, sites.length, contentTags.length])

  // Set default tag when tags are loaded and no tag is selected
  useEffect(() => {
    if (contentTags.length > 0 && !tag) {
      onTagChange({ target: { value: contentTags[0] } })
    }
  }, [contentTags, tag, onTagChange])

  return (
    <Stack spacing={2.5} sx={{ maxWidth: '100%' }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Titulo <Box component="span" sx={{ color: 'error.main' }}>*</Box>
      </Typography>
      <TextField
        size="medium"
        value={title}
        onChange={onTitleChange}
        fullWidth
        required
        onInvalid={(e) => {
          (e.target as HTMLInputElement).setCustomValidity('Por favor completa este campo')
        }}
        onInput={(e) => {
          (e.target as HTMLInputElement).setCustomValidity('')
        }}
        sx={{
          '& .MuiInputBase-root': {
            height: '40px',
          },
        }}
      />

      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Tag
        </Typography>
        <Select
          value={tag}
          onChange={onTagChange}
          fullWidth
          sx={{ height: '40px' }}
          disabled={tagsLoading || contentTags.length === 0}
        >
          {tagsLoading ? (
            <MenuItem disabled>
              <CircularProgress size={20} />
            </MenuItem>
          ) : (
            contentTags.map((tagItem) => (
              <MenuItem key={tagItem} value={tagItem}>
                {tagItem}
              </MenuItem>
            ))
          )}
        </Select>
      </Box>

      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Sede
        </Typography>
        <Select
          value={site}
          onChange={onSiteChange}
          fullWidth
          sx={{ height: '40px' }}
          disabled={sitesLoading || sites.length === 0}
        >
          {sitesLoading ? (
            <MenuItem disabled>
              <CircularProgress size={20} />
            </MenuItem>
          ) : (
            sites.map((item, index) => (
              <MenuItem key={item.id || index} value={item.Name}>
                {item.Name}
              </MenuItem>
            ))
          )}
        </Select>
      </Box>

      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Fecha <Box component="span" sx={{ color: 'error.main' }}>*</Box>
        </Typography>
        <DatePicker
          value={date && dayjs.isDayjs(date) ? date : null}
          onChange={onDateChange}
          minDate={dayjs()}
          desktopModeMediaQuery="@media (min-width: 0px)"
          slotProps={{
            textField: {
              fullWidth: true,
              size: 'small',
              error: dateError,
              sx: {
                '& .MuiInputBase-root': {
                  height: '40px',
                  padding: 0,
                  ...(dateError && {
                    borderColor: '#d32f2f',
                    '& fieldset': {
                      borderColor: '#d32f2f !important',
                      borderWidth: '2px',
                    },
                  }),
                },
                '& .MuiInputBase-input': {
                  padding: '8.5px 14px',
                },
              },
            },
            layout: {
              sx: {
                '& .MuiDateCalendar-root': {
                  width: '100%',
                  maxHeight: 'none',
                },
                '& .MuiPickersLayout-contentWrapper': {
                  alignItems: 'center',
                },
              },
            },
          }}
          format="DD-MM-YYYY"
        />
      </LocalizationProvider>

      <FileUploadSection
        contentType={contentType}
        image={image}
        document={document}
        onImageSelect={onImageSelect}
        onDocumentSelect={onDocumentSelect}
        onRemoveImage={onRemoveImage}
        onRemoveDocument={onRemoveDocument}
      />
    </Stack>
  )
}

export default NewsFormFields
